import { useEffect, useState } from "react"
import PropTypes from "prop-types";

import Indication, { IndicationState } from "components/atoms/indication/Indication";
import {
  CustomContainer,
  connect,
} from "components/atoms/DependencyHelper";
import CTADescription from "components/organisms/dealSpecifications/CTADescription";

import StockModal from "components/molecules/StockModal/StockModal";

import { handleSelect } from "Redux/actions/AlertAction";
import {
  link as AnalyticsLink,
  getUtagData as AnalyticsGetUtagData
} from "services/analyticsUtag";

import ajaxService from "utils/ajaxServices";
import {
  useDeviceStockStatus,
  useInclusionStockStatus,
} from "utils/hooks/dealDetails";
import {
  imagePrefix,
  getCookieValue,
} from "utils/helperServices";

import labels from "../../../config/labels";
import config from "../../../config/index";
import { AlertDetails } from "../../../config/queries";

import styles from "./dealDetailsIndicators.module.scss";

const IndicatorTypes = {
  ProductStock: "product-stock",
  DeliveryInfo: "delivery-info",
  TermsAndConditions: "Terms-and-conditions",
  UpgradeNotice: "upgrade-notice"
};

const addProductStatus = ({
  isDeviceInStock,
  isDeviceBackOrder,
  isInclusionInStock,
  isInclusionBackOrder,
  stockMessages,
  selectedOption
}) => {
  const productStatus = {
    id: "product-status",
    description: labels.lbl_In_the_stock_description,
    status: IndicationState.Good,
    type: IndicatorTypes.ProductStock,
    title: labels.deal_details.device_data.lbl_available_online,
  };

  if ((isDeviceInStock && isInclusionInStock) && (!isDeviceBackOrder && !isInclusionBackOrder)) {
    return productStatus;
  } else if (!isDeviceInStock || !isInclusionInStock) {
    productStatus.description = labels.lbl_out_of_stock_description
    productStatus.status = IndicationState.Bad;
    productStatus.title = labels.lbl_out_of_stock_title
    productStatus.action = {
      showAction: (selectedOption != labels.lbl_custom_dropdown_upgrade_option
        || labels.lbl_custom_dropdown_upgrade_option_new_contract )
        && selectedOption != labels.lbl_custom_dropdown_deal_option,
      actionLabel: "Notify me when it's available online"
    }

    return productStatus;
  } else if (isDeviceBackOrder) { // Back-order, primary product
    productStatus.description = typeof stockMessages?.out_of_stock_description === "string"
      ? stockMessages?.out_of_stock_description.replace("%product%", "product colour")
      : stockMessages?.out_of_stock_description;
    productStatus.status = IndicationState.Warning;
    productStatus.title = stockMessages?.out_of_stock_title;

    return productStatus;
  } else if (isInclusionBackOrder) { // Back-order, inclusion
    productStatus.description = typeof stockMessages?.out_of_stock_description === "string"
      ? stockMessages?.out_of_stock_description.replace("%product%", "accessory colour")
      : stockMessages?.out_of_stock_description;
    productStatus.status = IndicationState.Warning;
    productStatus.title = stockMessages?.out_of_stock_title;

    return productStatus;
  }

  return null;
}

const addUpgradeCheck = (isUpgradeSelection) => {
  if (!isUpgradeSelection) {
    return null;
  }

  return {
    id: "upgrade-notice",
    type: IndicatorTypes.UpgradeNotice,
  };
}

const addDeliveryInfo = (delivery_and_return, isSimonly, isUpgradeSelection) => {
  if (!delivery_and_return || (isSimonly && isUpgradeSelection)) {
    return null;
  }

  const termsRegex = (/terms *and *conditions/g);
  const isTsAndCs = typeof delivery_and_return === 'string'
    ? delivery_and_return.match(termsRegex)
    : false;

  return {
    id: "delivery-info",
    content: delivery_and_return,
    type: IndicatorTypes.DeliveryInfo,
    isTsAndCs: isTsAndCs
  };
}

const DealDetailsIndicators = (props) => {
  const { isInStock: isDeviceInStock, isBackOrder: isDeviceBackOrder } = useDeviceStockStatus();
  const { isInStock: isInclusionInStock, isBackOrder: isInclusionBackOrder }  = useInclusionStockStatus();

  const [showStockModal, setShowStockModal] = useState(false);

  const env = config.env;
  const loginUrl = config.envConfigs[env].DIDLoginUrl;

  const dealIndicators = [];

  dealIndicators.push(addProductStatus({
    isDeviceInStock,
    isDeviceBackOrder,
    isInclusionInStock,
    isInclusionBackOrder,
    stockMessages: props.stockMessages,
  }));
  dealIndicators.push(addUpgradeCheck(props.isUpgradeSelection));
  dealIndicators.push(addDeliveryInfo(props.deliveryAndReturn, props.isSimonly, props.isUpgradeSelection));

  const colCount = dealIndicators.reduce(
    (sum, cur) => cur === null ? sum : sum + 1,
    0
  );
  const isGridView = colCount > 2
    && typeof props.deliveryAndReturn === "string"
    && props.deliveryAndReturn.length > 0;

  async function getAlertData() {
    const alertResult = await ajaxService.fetchGraphQLPOSTRequest(
      AlertDetails,
      {
        productId: props.dealDetailID,
      }
    );

    if (alertResult.productAlert == null) {
      setShowStockModal(false);
    } else {
      setShowStockModal(true);
    }

    props.handleAlertSelect(false);
  }
  
  const handleModalClose = () => {
    setShowStockModal(false);
    AnalyticsLink({
      ...AnalyticsGetUtagData([
        "page_name",
        "page_channel",
        "page_master_tab",
        "sales_product_id",
        "sales_product_price",
        "sales_product_quantity",
        "visitor_login_status",
        "visitor_msisdn_hash",
        "visitor_customer_id",
        "visitor_id_hash",
        "visitor_subscription_type",
        "page_site_section_lvl_1",
        "page_site_section_lvl_2",
        "page_site_section_lvl_3",
        "page_site_section_lvl_4",
      ]),
      page_form_event: "success",
      page_form_name:
        "shopping: devices: out of stock: email notification signup",
    });
  };

  const handleModalShow = async () => {
    props.handleAlertSelect(true);

    const analyticsData = {
      ...AnalyticsGetUtagData([
        "page_name",
        "page_channel",
        "page_master_tab",
        "sales_product_id",
        "sales_product_price",
        "sales_product_quantity",
        "sales_product_category",
        "visitor_login_status",
        "visitor_msisdn_hash",
        "visitor_customer_id",
        "visitor_id_hash",
        "visitor_subscription_type",
        "page_site_section_lvl_1",
        "page_site_section_lvl_2",
        "page_site_section_lvl_3",
        "page_site_section_lvl_4",
      ]),
      link_type: "o",
      link_name: `shopping: devices: deal details: out of stock: notify me when its available online`,
    };
    AnalyticsLink(analyticsData);
  };

  useEffect(() => {
    const JWTtoken = getCookieValue("vod-web-auth-token");
    if (props.checkSelect === true) {
      if (props.userLoggedIn && JWTtoken && props.dealDetailID) {
        getAlertData();
      } else if (!JWTtoken && !props.profileLoading && !props.userLoggedIn) {
        let redirectURL = loginUrl + "?redirectUrl=" + window.location.href + (props.isMiniApp ? "&app=true" : "");
        window.location.replace(redirectURL);
      }
    }
  }, [props.checkSelect, props.userLoggedIn, props.profileLoading]);

  const renderIndicator = (indicatorItem) => {
    if (!indicatorItem) {
      return null;
    }

    switch (indicatorItem.type) {
      case IndicatorTypes.DeliveryInfo:
        return (
            <CustomContainer
              key={indicatorItem.id}
              dangerouslySetInnerHTML={{
                __html: indicatorItem.content,
              }}
              className={`${styles["deliveryReturn_section"]} 
                ${isGridView ? styles["deliveryReturn_section--enhanced"] : ""} 
                ${indicatorItem.isTsAndCs ? styles["deliveryReturn_section--terms"] : ""}
              `}
            />
        );
      case IndicatorTypes.ProductStock:
        return (
          <Indication
            key={indicatorItem.id}
            className={styles["indicator-item"]}
            type={indicatorItem.status}
            state={indicatorItem.title}
            description={indicatorItem.description}
            uniqueIndication={indicatorItem.id}
            actionLabel={indicatorItem?.action?.actionLabel}
            showAction ={indicatorItem?.action?.showAction}
            onActionClick={handleModalShow}
          />
        );
      case IndicatorTypes.UpgradeNotice:
        return (
          <CustomContainer className={styles["upgrade-indicator"]}>
            <CTADescription
              imagePrefix={imagePrefix}
              isUpgradeEnabled={props.isUpgradeEnabled}
              handleFindOut={props.onUpgradeAction}
            />
          </CustomContainer>
        );
      default:
        return null;
    }
  }

  return (
    <CustomContainer className={`${styles["indicators-container"]} ${props.className}`}>
        <StockModal
          show={showStockModal}
          handleClose={handleModalClose}
        />

        { dealIndicators.map(renderIndicator) }
    </CustomContainer>
  );
};

DealDetailsIndicators.defaultProps = {
  className: "",
  stockMessages: {},
};

DealDetailsIndicators.propTypes = {
  className: PropTypes.string,
  deliveryAndReturn: PropTypes.string,
  isUpgradeSelection: PropTypes.bool,
  isUpgradeEnabled: PropTypes.bool,
  onUpgradeAction: PropTypes.func,
  stockMessages: PropTypes.shape({
    low_stock_description: PropTypes.string,
    low_stock_title: PropTypes.string,
    out_of_stock_description: PropTypes.string,
    out_of_stock_title: PropTypes.string,
  }),

  // Redux
  checkSelect: PropTypes.bool,
  dealDetailID: PropTypes.string,
  isMiniApp: PropTypes.bool,
  profileLoading: PropTypes.bool,
  userLoggedIn: PropTypes.bool,
  handleAlertSelect: PropTypes.func,
  isSimonly: PropTypes.bool
};

const mapStateToProps = (state) => ({
  userLoggedIn: state.navDetails.isUserLoggedIn,
  checkSelect: state.alertSelect.alert,
  dealDetailID: state.dealDetails.id,
  profileLoading: state.navDetails.isProfileLoading,
  isSimonly: state.dealDetails.selectedDealDetails.sim_only_flg,
  isMiniApp: state.navDetails.isMiniApp,
});

const mapDispatchToProps = {
  handleAlertSelect: handleSelect,
};

export default connect(mapStateToProps, mapDispatchToProps)(DealDetailsIndicators);
